<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import { accountTypeLabel, formatMoney } from '../utils/format'
import type { CurrencyOption, FinancialAccount } from '../types/finance'

const { t } = useI18n()
const auth = useAuthStore()
const { listFinancialAccounts, listCurrencies } = useFinanceApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('financial_accounts.manage'))
const canViewLedger = computed(() => permissions.value.includes('treasury_transactions.view'))

const rows = ref<FinancialAccount[]>([])
const currencies = ref<CurrencyOption[]>([])
const loading = ref(true)
const error = ref('')

const filters = ref({
  currency_code: '',
  is_active: '1' as string | number,
})

const totalsByCurrency = computed(() => {
  const totals = new Map<string, number>()
  for (const account of rows.value) {
    const code = account.currency_code || '—'
    totals.set(code, (totals.get(code) ?? 0) + Number(account.current_balance ?? 0))
  }
  return [...totals.entries()]
    .map(([code, amount]) => ({ code, amount }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    rows.value = await listFinancialAccounts({
      currency_code: filters.value.currency_code,
      is_active: filters.value.is_active as '' | 0 | 1,
    })
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadCurrencies() {
  try {
    currencies.value = await listCurrencies()
  } catch {
    currencies.value = []
  }
}

onMounted(() => {
  void loadCurrencies()
  void load()
})

watch(
  () => [filters.value.currency_code, filters.value.is_active],
  () => {
    void load()
  },
)
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.accounts.title') }}</h2>
  </div>

  <div class="toolbar">
    <select v-model="filters.currency_code" class="filter-select">
      <option value="">{{ t('finance.common.allCurrencies') }}</option>
      <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
        {{ currency.code }} — {{ currency.name }}
      </option>
    </select>
    <select v-model="filters.is_active" class="filter-select">
      <option value="1">{{ t('finance.common.active') }}</option>
      <option value="0">{{ t('finance.common.inactive') }}</option>
      <option value="">{{ t('finance.common.all') }}</option>
    </select>
    <span class="spacer" />
    <div class="summary">
      <span v-for="line in totalsByCurrency" :key="line.code">
        <strong>{{ formatMoney(line.amount, line.code) }}</strong>
      </span>
      <span>{{ t('finance.common.rows', { count: rows.length }) }}</span>
    </div>
    <RouterLink v-if="canManage" class="btn-primary" to="/treasury/accounts/new">
      {{ t('finance.accounts.newAccount') }}
    </RouterLink>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <div v-else-if="rows.length === 0" class="state-message">
    <p>{{ t('finance.accounts.empty') }}</p>
    <RouterLink v-if="canManage" class="btn-primary" to="/treasury/accounts/new">
      {{ t('finance.accounts.newAccount') }}
    </RouterLink>
  </div>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.accounts.accountCode') }}</th>
            <th>{{ t('finance.accounts.accountName') }}</th>
            <th>{{ t('finance.accounts.accountType') }}</th>
            <th>{{ t('finance.common.currency') }}</th>
            <th>{{ t('finance.accounts.department') }}</th>
            <th class="cell-num">{{ t('finance.common.openingBalance') }}</th>
            <th class="cell-num">{{ t('finance.common.currentBalance') }}</th>
            <th>{{ t('finance.common.status') }}</th>
            <th class="cell-actions">{{ t('finance.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="account in rows" :key="account.id">
            <td class="cell-code">{{ account.account_code }}</td>
            <td class="cell-name">
              {{ account.name }}
              <span v-if="account.branch_name" class="cell-sub">{{ account.branch_name }}</span>
            </td>
            <td>{{ accountTypeLabel(t, account.account_type) }}</td>
            <td>{{ account.currency_code }}</td>
            <td>{{ account.department_name || '—' }}</td>
            <td class="cell-num">{{ formatMoney(account.opening_balance, account.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(account.current_balance, account.currency_code) }}</td>
            <td>
              <span class="status-badge" :class="account.is_active === 1 ? 'status-ok' : 'status-neutral'">
                {{ account.is_active === 1 ? t('finance.common.active') : t('finance.common.inactive') }}
              </span>
            </td>
            <td class="cell-actions">
              <RouterLink
                v-if="canViewLedger"
                class="link-action"
                :to="`/treasury/accounts/${account.id}`"
              >
                {{ t('finance.accounts.transactions') }}
              </RouterLink>
              <RouterLink
                v-if="canManage"
                class="link-action"
                :to="`/treasury/accounts/${account.id}/edit`"
              >
                {{ t('finance.common.edit') }}
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>
</template>
